/**
 * Planetary placement interpretation engine.
 * Produces psychologically meaningful interpretations with:
 * core function, sign expression, house arena, strength, challenge, developmental invitation.
 */

const PLANET_FUNCTIONS = {
  sun: { core: 'core identity, vitality, and creative will', focus: 'how identity organizes decisions' },
  moon: { core: 'emotional needs, instinctive reactions, and internal safety', focus: 'how needs drive reactions' },
  mercury: { core: 'thinking, language, and interpretation', focus: 'how interpretation shapes outcomes' },
  venus: { core: 'relating, preference, and value selection', focus: 'how values direct relationship choices' },
  mars: { core: 'drive, protection, and decisive action', focus: 'how force is mobilized or contained' },
  jupiter: { core: 'expansion, belief, and perspective', focus: 'how belief opens or overextends paths' },
  saturn: { core: 'structure, pressure, and maturation', focus: 'where pressure builds skill over time' },
  uranus: { core: 'disruption, originality, and liberation impulse', focus: 'where disruption restores autonomy' },
  neptune: { core: 'imagination, permeability, and symbolic sensitivity', focus: 'where imagination blurs or reveals reality' },
  pluto: { core: 'deep transformation, control, and regeneration', focus: 'where depth work transforms your stance' },
  northnode: { core: 'growth direction and developmental edge', focus: 'where growth asks for unfamiliar behavior' },
  lilith: { core: 'untamed instinct and uncompromised truth', focus: 'where unfiltered truth pushes against control' },
  chiron: { core: 'wound-healing intelligence and restorative skill', focus: 'where healing intelligence appears through repetition' },
};

const SIGN_EXPRESSIONS = {
  Aries: 'direct initiation, impatience, and assertive self-assertion',
  Taurus: 'steadiness, embodiment, and resistance to sudden change',
  Gemini: 'curiosity, mental flexibility, and branching attention',
  Cancer: 'emotional memory, protectiveness, and sensitivity to atmosphere',
  Leo: 'visibility, pride, and heart-led creative risk',
  Virgo: 'precision, refinement, and attention to practical detail',
  Libra: 'relational calibration, harmony-seeking, and response to imbalance',
  Scorpio: 'depth, intensity, and psychological honesty',
  Sagittarius: 'meaning-seeking, expansion, and appetite for truth',
  Capricorn: 'restraint, responsibility, and strategic building',
  Aquarius: 'detachment, perspective-shifting, and nonconformist choice',
  Pisces: 'permeability, imagination, and symbolic processing',
};

const HOUSE_ARENAS = {
  1: 'identity, body, and first impression',
  2: 'resources, self-worth, and what you protect as yours',
  3: 'communication, learning, and local environment',
  4: 'home, roots, and emotional foundation',
  5: 'creativity, romance, visibility, and personal voice',
  6: 'daily work, health habits, and skill refinement',
  7: 'partnership, contracts, and relational mirrors',
  8: 'shared bonds, vulnerability, and psychological depth',
  9: 'belief, long-range study, and worldview formation',
  10: 'vocation, public role, and directional ambition',
  11: 'community, friendship, and future-oriented goals',
  12: 'retreat, closure cycles, and private emotional processing',
};

const SIGN_STRENGTHS = {
  Aries: 'bold initiation and honest directness',
  Taurus: 'steady presence and embodied reliability',
  Gemini: 'mental agility and communicative curiosity',
  Cancer: 'emotional attunement and protective care',
  Leo: 'creative visibility and heart-led expression',
  Virgo: 'practical refinement and discerning attention',
  Libra: 'relational intelligence and balance-seeking',
  Scorpio: 'psychological depth and transformative honesty',
  Sagittarius: 'meaning-making and expansive perspective',
  Capricorn: 'durable building and responsible follow-through',
  Aquarius: 'fresh perspective and principled independence',
  Pisces: 'symbolic sensitivity and imaginative permeability',
};

const SIGN_CHALLENGES = {
  Aries: 'impatience, impulsivity, or difficulty pausing',
  Taurus: 'rigidity, over-attachment, or resistance to change',
  Gemini: 'scattered focus or difficulty landing on one thread',
  Cancer: 'over-protectiveness or emotional overwhelm',
  Leo: 'pride, performance pressure, or need for constant validation',
  Virgo: 'over-criticism, perfectionism, or anxiety about detail',
  Libra: 'indecision, people-pleasing, or avoidance of conflict',
  Scorpio: 'control, intensity that overwhelms, or trust issues',
  Sagittarius: 'overreach, blunt honesty, or restlessness',
  Capricorn: 'over-restraint, pessimism, or workaholic tendencies',
  Aquarius: 'detachment that distances, or rebelliousness for its own sake',
  Pisces: 'boundary confusion, escapism, or losing oneself in others',
};

const PLANET_DEVELOPMENTAL = {
  sun: 'refining what you stand for and how you show up',
  moon: 'learning to meet your needs without abandoning others',
  mercury: 'distinguishing interpretation from fact',
  venus: 'choosing what deserves your attention and care',
  mars: 'channeling drive without destructiveness',
  jupiter: 'expanding without overextending',
  saturn: 'building through limits without collapsing into fear',
  uranus: 'breaking patterns that serve liberation, not chaos',
  neptune: 'honoring imagination while staying grounded',
  pluto: 'transforming without destroying',
  northnode: 'stepping toward the unfamiliar',
  lilith: 'speaking truth without self-sabotage',
  chiron: 'turning wound into craft',
};

function ordinalHouse(value) {
  const h = Number(value);
  if (h === 1) return '1st';
  if (h === 2) return '2nd';
  if (h === 3) return '3rd';
  return `${h}th`;
}

function subjectLabel(name) {
  if (name === 'Sun' || name === 'Moon' || name === 'North Node') return `The ${name}`;
  return name || 'This planet';
}

export function generatePlacementInterpretation({ placement }) {
  if (!placement) return null;
  const key = String(placement.key || '').toLowerCase();
  const planet = PLANET_FUNCTIONS[key] || { core: 'a core psychological function', focus: 'how this function operates' };
  const signExpr = SIGN_EXPRESSIONS[placement.sign] || `${placement.sign} expression`;
  const arena = HOUSE_ARENAS[placement.house] || 'daily life arenas';
  const h = ordinalHouse(placement.house);
  const strength = SIGN_STRENGTHS[placement.sign] || 'natural expression through this sign';
  const challenge = SIGN_CHALLENGES[placement.sign] || 'distortion or overdoing of this sign\'s themes';
  const developmental = PLANET_DEVELOPMENTAL[key] || 'integrating this function more consciously';
  const label = subjectLabel(placement.name || placement.key);

  const corePattern = `${label} represents ${planet.core}. In ${placement.sign}, that function tends toward ${signExpr}. In the ${h} house, this becomes visible especially in ${arena}.`;

  const possibleExpressions = [
    `A natural strength here can be ${strength}.`,
    `A possible challenge is ${challenge}.`,
    `This placement often invites ${developmental}.`,
  ];

  const observation = `Notice ${planet.focus} — particularly in ${arena}. The ${placement.sign} coloring means you may respond through ${signExpr}. What repeats in this area usually reveals the real teaching.`;

  return {
    corePattern,
    possibleExpressions,
    observation,
  };
}

export function getHouseArenaText(house) {
  return HOUSE_ARENAS[house] || 'daily life arenas';
}
